import { defineStore } from "pinia";
import type {
  LoginDto,
  LoginMutation,
  LoginMutationVariables,
  User,
} from "~/types/graphql";
import login from "@/graphql/mutations/login.graphql";
import me from "@/graphql/queries/me.graphql";

export const useAuth = defineStore("auth", {
  state: () => ({
    user: null as User | null,
    loadingUser: false,
    sessionProvider: null as "local" | "keycloak" | null,
    loadingSessionProvider: false,
  }),

  getters: {
    authenticated: (state): boolean => !!state.user,
  },

  actions: {
    async fetchUser(): Promise<User | null> {
      this.loadingUser = true;
      try {
        const { data } = await useAsyncQuery<{me: User }>({ query: me });
        this.user = data.value?.me ?? null;
        return this.user;
      } catch (error) {
        this.user = null;
        return null;
      } finally {
        this.loadingUser = false;
      }
    },

    async fetchSessionProvider(): Promise<"local" | "keycloak" | null> {
      if (this.loadingSessionProvider) {

        return this.sessionProvider;
      }
      this.loadingSessionProvider = true;

      try {
        const config = useRuntimeConfig();
        const apiBase = (config.public as any).BACKEND_URL ?? "/api";
        const response = await fetch(`${apiBase}/auth/session`, {
          method: 'GET',
          credentials: 'include',
          headers: { 'Accept': 'application/json' },
        });
        if (!response.ok) {
          this.sessionProvider = null;
          return this.sessionProvider;
        }
        const data = await response.json();
        const provider = data?.provider;
        if (provider === 'local' || provider === 'keycloak') {
          this.sessionProvider = provider;
        } else {
          this.sessionProvider = null;
        }
        return this.sessionProvider;
      } catch (error) {
        this.sessionProvider = null;
        return this.sessionProvider;
      } finally {
        this.loadingSessionProvider = false;
      }
    },

    startKeycloakLogin(returnTo?: string) {
      const config = useRuntimeConfig();
      const authMode = (config.public as any).AUTH_MODE ?? "keycloak";
      if (authMode !== "keycloak") {
        return;
      }
      const route = useRoute();
      const target = returnTo ?? route.fullPath;
      const encoded = encodeURIComponent(target);

      // Use either apiBase, or fall back to "/api"
      const apiBase = (config.public as any).BACKEND_URL ?? "/api";
      window.location.replace(`${apiBase}/auth/login-openid?returnTo=${encoded}`);
    },

    async logout(): Promise<void> {
      const config = useRuntimeConfig();
      const apiBase = (config.public as any).BACKEND_URL ?? "/api";
      this.user = null;
      this.sessionProvider = null;

      // Stop background token refresh when logging out
      try {
        const tokenRefresh = useTokenRefresh();
        tokenRefresh.stopRefresh();
      } catch (e) {
        // Composable may not be available in SSR context
        console.debug('[Auth] Token refresh not available');
      }

      window.location.replace(`${apiBase}/auth/logout-openid`);
    },

    // Local login
    async login(creds: LoginDto) {
      const res = await storeGraphqlMutation<LoginMutation, LoginMutationVariables>(
        {
          mutation: login,
          variables: {
            password: creds.password,
            username: creds.username,
          },
        },
        undefined,
        false,
      );

      const token = useCookie<string | null>("accessToken");
      if (!token.value) {
        throw new Error("No token found");
      }

      await this.fetchUser();
      await this.fetchSessionProvider();

      return res;
    },
  },
});
